import { useMemo } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { resolveScore, resolveLabel, lastTwoSentences } from '../../utils/moodScale';
import { EMOTION_META, isEmotion } from '../../utils/emotions';

const SAGE = '#7C9A82';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatDate = (ts) => {
  const d = new Date(ts);
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

// Ranks echoes by mood shift (post − pre). Ties broken by more recent first.
// Only positive shifts qualify — a negative shift isn't a "breakthrough".
function pickBreakthroughs(echoes, limit = 3) {
  if (!echoes || echoes.length === 0) return [];
  return echoes
    .map((e) => {
      const preScore = resolveScore(e.emotion_before, e.mood_before);
      const postScore = resolveScore(e.emotion_after, e.mood_after);
      // null on either side means the note was never labelled there, so there
      // is no shift to claim. NaN would survive the `shift > 0` filter below on
      // some engines' comparison rules; an explicit null never does.
      const shift =
        preScore === null || postScore === null
          ? null
          : Math.round((postScore - preScore) * 10) / 10;
      const ts = new Date(e.created_at).getTime();
      const summarySource =
        e.user_edited_text || e.accepted_text || e.raw_transcript || '';
      return {
        id: e.id,
        shift,
        ts,
        labelBefore: resolveLabel(e.emotion_before, e.mood_before),
        labelAfter: resolveLabel(e.emotion_after, e.mood_after),
        emotionBefore: isEmotion(e.emotion_before) ? e.emotion_before : null,
        emotionAfter: isEmotion(e.emotion_after) ? e.emotion_after : null,
        summary: lastTwoSentences(summarySource),
      };
    })
    .filter((e) => e.shift !== null && e.shift > 0 && Number.isFinite(e.ts))
    .sort((a, b) => (b.shift - a.shift) || (b.ts - a.ts))
    .slice(0, limit);
}

function EmotionTag({ code, fallbackLabel }) {
  const meta = code ? EMOTION_META[code] : null;
  if (!meta) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300">
        {fallbackLabel || '—'}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: meta.bg, color: meta.fg }}
    >
      {meta.label}
    </span>
  );
}

function BreakthroughCard({ item }) {
  return (
    <article
      data-testid="breakthrough-card"
      className="shrink-0 w-72 sm:w-auto rounded-2xl bg-white dark:bg-stone-800/40 border border-stone-200/60 dark:border-stone-700/40 shadow-sm p-4 sm:p-5 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md"
    >
      <header className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-stone-500 dark:text-stone-400 tabular-nums">
          {formatDate(item.ts)}
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white tabular-nums"
          style={{ backgroundColor: SAGE }}
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          +{item.shift} pts
        </span>
      </header>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400 flex-wrap">
        <EmotionTag code={item.emotionBefore} fallbackLabel={item.labelBefore} />
        <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
        <EmotionTag code={item.emotionAfter} fallbackLabel={item.labelAfter} />
      </div>
      <p className="mt-3 text-sm leading-relaxed text-stone-700 dark:text-stone-200 italic line-clamp-2">
        {item.summary || 'A quiet check-in.'}
      </p>
    </article>
  );
}

export default function RecentBreakthroughs({ echoes, onViewTimeline }) {
  const items = useMemo(() => pickBreakthroughs(echoes, 3), [echoes]);

  if (items.length === 0) return null;

  return (
    <section data-testid="breakthroughs">
      <header className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-sm sm:text-base font-semibold tracking-tight text-stone-800 dark:text-stone-100">
          Recent Breakthroughs
        </h3>
        {onViewTimeline && (
          <button
            type="button"
            onClick={onViewTimeline}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#4a6850] dark:text-[#A9C4AF] hover:opacity-80 transition-opacity"
          >
            View timeline
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </header>
      <div className="flex sm:grid sm:grid-cols-3 gap-3 sm:gap-4 overflow-x-auto sm:overflow-visible pb-1 sm:pb-0 -mx-1 px-1 snap-x snap-mandatory sm:snap-none">
        {items.map((item) => (
          <div key={item.id} className="snap-start">
            <BreakthroughCard item={item} />
          </div>
        ))}
      </div>
    </section>
  );
}

export { pickBreakthroughs };
